import { Localized } from "@fluent/react"
import { Alert, Autocomplete, Avatar, Box, Dialog, DialogActions, DialogContent, DialogTitle, ListItemAvatar, ListItemText, MenuItem, Skeleton, TextField, Typography } from "@mui/material"
import { useCallback, useMemo, useState } from "react"
import { LoadUsers } from "../../../../app/AppDataLoader"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { If } from "../../../../app/If"
import { store } from "../../../../app/store"
import { selectAllUsers, selectUserByName, User } from "../../../users/usersSlice"
import { AppId, AppTextFieldHandler } from "../../../../app/appTypes"
import { ProjectProcessTeamMember, createProcessTeamMember } from "../ProjectProcessApiClient"
import { escapeRegExp } from "@mui/x-data-grid/internals"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"

export interface ProcessTeamMemberDialogProps {
    processId: AppId
    mode: "create" | "edit"
    defaultUserName: string
    excludedUsers: Set<string>
    onSave: (tm: ProjectProcessTeamMember) => void
    onCancel: () => void,
}


export const ProcessTeamMemberDialog = ({ processId, mode, defaultUserName, onSave, onCancel, excludedUsers }: ProcessTeamMemberDialogProps) => {
    const dispatch = useAppDispatch()
    const allUsers = useAppSelector(selectAllUsers)
    const [user, setUser] = useState<User | null>(() => selectUserByName(store.getState(), defaultUserName) ?? null)
    const [userRole, setUserRole] = useState<string>("")
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const handleUserChange = useCallback((_, newUser) => {
        setUser(newUser)
    }, [])

    const handleUserRoleChange: AppTextFieldHandler = useCallback(e => setUserRole(e.target.value), [setUserRole])

    const availableUsers =
        useMemo(() => allUsers.filter(u => !excludedUsers.has(u.userName) || u.userName === defaultUserName), [allUsers, excludedUsers, defaultUserName])

    const initials = (first?: string, last?: string) =>
        [first?.[0], last?.[0]].filter(Boolean).join('')?.toUpperCase() || ' ';
    const [inputValue, setInputValue] = useState('');

    const highlight = (text: string, query: string) => {
        if (!query) return <>{text}</>;
        try {
            const parts = text.split(new RegExp(`(${escapeRegExp(query)})`, 'ig'));
            return (
                <>
                    {parts.map((p, i) =>
                        p.toLowerCase() === query.toLowerCase()
                            ? <strong key={i}>{p}</strong>
                            : <span key={i}>{p}</span>
                    )}
                </>
            );
        } catch {
            return <>{text}</>;
        }
    };

    const handleOkClick = () => {
        if (user !== null) {
            setSaving(true)
            switch (mode) {
                case "create":
                    handleCreate()
                    break
                case "edit":
                    break
            }
        }
    }
    const handleCreate = async () => {
        if (user !== null) {
            setSaving(true);
            try {
                const result = await dispatch(createProcessTeamMember({ user: user.userName, processId, role: userRole }));

                if (createProcessTeamMember.fulfilled.match(result)) {
                    const newTeamMember = result.payload as ProjectProcessTeamMember;

                    dispatch(showSuccess("sucess"));
                    onSave(newTeamMember);
                } else {
                    let errorMsg = "error";
                    if (result.payload && result.payload.kind === "http") {
                        const problem = result.payload.problem;
                        if (problem) {
                            errorMsg = problem.title;
                        }
                    }
                    setServerError(errorMsg);
                }
            } catch (error) {
                dispatch(showError("error"));
            } finally {
                setSaving(false);
            }
        }
    };


    return <>
        <Dialog open={true} fullWidth maxWidth='sm'>
            <DialogTitle sx={{
                bgcolor: "grey.100",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
            }}>
                <If condition={mode === "create"}>
                    <PersonAddAlt1RoundedIcon color="success" />

                    <Localized id="urs-add-team-member">
                        <span>Nowy członek zespołu</span>
                    </Localized>
                </If>
                <If condition={mode === "edit"}>
                    <EditRoundedIcon color="info" />

                    <Localized id="urs-edit-team-member">
                        <span>Edycja członka zespołu</span>
                    </Localized>
                </If>
            </DialogTitle>
            <DialogContent sx={{
                padding: 2,
                mt: 3
            }}>
                <LoadUsers component={<Skeleton />}>
                    <Autocomplete
                        sx={{ mt: 1 }}
                        options={availableUsers}
                        value={user}
                        onChange={handleUserChange}
                        inputValue={inputValue}
                        onInputChange={(_, v) => setInputValue(v)}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        getOptionLabel={({ firstname, surname, email }) =>
                            `${surname} ${firstname} (${email})`
                        }
                        groupBy={(o) => o.companyName ?? '—'}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={(
                                    <Typography component="span">Nazwa użytkownika</Typography>
                                )}
                                helperText={user?.position ?? ' '}
                                placeholder="Wpisz nazwisko, imię lub email…"
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                <ListItemAvatar>
                                    <Avatar src={undefined}>
                                        {initials(option.firstname, option.surname)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box display="flex" gap={1} alignItems="center">
                                            <Typography variant="body2" fontWeight={600}>
                                                {highlight(`${option.surname} ${option.firstname}`, inputValue)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {highlight(`(${option.email})`, inputValue)}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary">
                                            {option.position || '—'}
                                        </Typography>
                                    }
                                />
                            </li>
                        )}
                        autoHighlight
                        blurOnSelect
                        clearOnEscape
                        filterSelectedOptions
                    />
                </LoadUsers>
                <TextField
                    sx={{
                        marginTop: 1,
                    }}
                    value={userRole}
                    fullWidth
                    required={true}
                    select
                    label={<Localized id="urs-user-role"><span>Rola</span></Localized>}
                    onChange={handleUserRoleChange}
                >
                    <MenuItem value={"C"}><Localized id="project-role-collaborator"><span>Collaborator</span></Localized></MenuItem>
                    <MenuItem value={"O"}><Localized id="project-role-observer"><span>Observer</span></Localized></MenuItem>
                </TextField>
                {serverError && (
                    <Alert
                        sx={{ mt: 2 }}
                        onClose={() => {
                            setServerError("");
                        }}
                        severity="error"
                    >
                        <Localized id={serverError}>
                            <span>Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <SaveButton loading={saving} onClick={handleOkClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    </>
}
